import { useMemo, useState } from 'react'

import { useTheme } from '@emotion/react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, PaddingInset, Stack, Text } from '@cash-design-system/react'
import { AllocationDistribution } from '@cash-web/protos/squareup/cash/paychecks/AllocationDistribution.pb'
import { AllocationDestination } from '@cash-web/protos/squareup/cash/paychecks/common.pb'
import { SetPaycheckMultiAllocationBlocker } from '@cash-web/protos/squareup/franklin/blockers/set_paycheck_multi_allocation_blocker.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSubmitSetPaycheckMultiAllocation } from '@cash-web/shared-data-access-api/react-query'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { useCurrencySymbol } from '@cash-web/shared-feature-currency-symbol'
import {
  ModalActionHeader,
  ModalBackAction,
  ModalCloseAction,
  ModalPaneHeader,
  useModalActionsOverride,
} from '@cash-web/shared-feature-modal'
import {
  DistributionType,
  getDestinationId,
  HeroVariant,
  NoRemainingAllocationModal,
} from '@cash-web/shared-feature-paychecks'
import { createArcs, PieChart, PieChartLegend, Slice } from '@cash-web/shared-feature-pie-chart'
import { useJourneyContext, useJourneyScope } from '@cash-web/shared-journey-data-access-context'
import { useColorProtoAdapter } from '@cash-web/shared-proto-adapters-ui-color'

import { BaseBlocker } from '../../BaseBlocker/BaseBlocker'
import { EditAllocation } from './EditAllocation'
import { getDisplayNameFromAllocationDestination } from './getDisplayNameFromAllocationDestination'

interface BlockerMultiAllocationProps {
  blocker: SetPaycheckMultiAllocationBlocker
}

export type Screen = 'home' | 'allocationEdit'

export const BlockerMultiAllocation = ({ blocker }: BlockerMultiAllocationProps) => {
  const isMultiAllocationBlockerEnabled = useFeatureFlag('cash-web-account-enable-multi-allocation-blocker')
  const { t } = useTranslation()
  const journey = useJourneyContext()
  const journeyScope = useJourneyScope()
  const { spacing } = useTheme()
  const { getColorValue } = useColorProtoAdapter()
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const [screen, setScreen] = useState<Screen>('home')
  const [isNoRemainingAllocationModalOpen, setIsNoRemainingAllocationModalOpen] = useState(false)
  const [allocationData, setAllocationData] = useState<{
    label: string
    allocation_destination: AllocationDestination
    color: string
    value: number
  } | null>(null)

  if (!isMultiAllocationBlockerEnabled) {
    throw Error('MultiAllocation blocker encountered but not enabled.')
  }

  const form = useForm<AllocationDistribution>({ defaultValues: { allocations: blocker.distribution?.allocations } })
  const allocations = form.watch('allocations') ?? []
  const { mutateAsync: submitSetPaycheckMultiAllocation } = useMutationSubmitSetPaycheckMultiAllocation()
  const {
    editable_allocations = [],
    allocation_tint_colors = [],
    editable_distribution_allocation_start_index,
  } = blocker

  if (
    editable_allocations === undefined ||
    allocation_tint_colors === undefined ||
    editable_distribution_allocation_start_index === undefined
  ) {
    throw Error('invalid MultiAllocation blocker data')
  }

  const editableAllocationCount = editable_allocations.length
  const startIndex = editable_distribution_allocation_start_index ?? 0
  const editableAllocations = allocations.slice(startIndex, startIndex + editableAllocationCount) ?? []

  const totalEditableAllocationPercentage =
    editableAllocations.reduce((acc, { share_in_basis_points }) => acc + (share_in_basis_points ?? 0), 0) / 100

  const currencySymbol = useCurrencySymbol()
  const averageMonthlyPaycheckAmount = blocker.average_monthly_paycheck_amount?.amount ?? 0
  const contributionBasedOnAverage = averageMonthlyPaycheckAmount * (totalEditableAllocationPercentage / 10000)
  const contributionBasedOnAverageParsed = `${currencySymbol}${parseFloat(`${contributionBasedOnAverage}`).toFixed(2)}`
  const estimatedCurrencyAmountTemplate = blocker.estimated_currency_amount_template?.android?.translated_value
  const estimatedCurrencyAmountText = estimatedCurrencyAmountTemplate?.replace('%s', contributionBasedOnAverageParsed)

  const arcs: Slice[] = []
  const legend: DistributionType[] = []
  const TWENTY_PERCENT_OPACITY = 33

  allocations.forEach(({ allocation_destination, share_in_basis_points }, index) => {
    const color = getColorValue(allocation_tint_colors[index])
    if (!allocation_destination || share_in_basis_points === undefined || color === undefined) return
    const destinationId = getDestinationId(allocation_destination)
    if (destinationId === undefined) return

    const isEditable = index >= startIndex && index <= startIndex + (editableAllocationCount - 1)
    const percentage = (share_in_basis_points ?? 0) / 100

    if (!isEditable) {
      arcs.push({
        id: destinationId,
        label: destinationId,
        percentage,
        color: `${color}${destinationId === 'cashBalance' ? '' : TWENTY_PERCENT_OPACITY}`,
      })
      return
    }

    const displayName = getDisplayNameFromAllocationDestination(allocation_destination)

    if (displayName === undefined) return

    if (index === editable_distribution_allocation_start_index) {
      arcs.push({
        id: 'stocks',
        label: 'stocks',
        percentage: totalEditableAllocationPercentage,
        segments: [
          { label: displayName, percentage: share_in_basis_points / 100 / totalEditableAllocationPercentage, color },
        ],
      })
    } else if (
      index > editable_distribution_allocation_start_index &&
      index < editable_distribution_allocation_start_index + editableAllocationCount
    ) {
      arcs[editable_distribution_allocation_start_index].segments?.push({
        label: displayName,
        percentage: share_in_basis_points / 100 / totalEditableAllocationPercentage,
        color,
      })
    }

    legend.push({
      id: displayName,
      color,
      label: displayName,
      percentage: share_in_basis_points / 100,
      value: `${share_in_basis_points / 100}%`,
      action: () => {
        const remainingAllocationAmount = allocations.reduce((acc, distribution) => {
          return acc - (distribution.share_in_basis_points ?? 0) / 100
        }, 100)
        if (remainingAllocationAmount <= 0 && percentage === 0) {
          setIsNoRemainingAllocationModalOpen(true)
        } else {
          setScreen('allocationEdit')
          setAllocationData({ label: displayName, allocation_destination, color, value: share_in_basis_points })
        }
      },
      flagZeroValues: true,
      variant: 'actionable',
    })
  })

  const handleSubmit = async () => {
    await handleBlockerResponse({
      promisedBlockerUpdate: submitSetPaycheckMultiAllocation({
        submit_all: { allocation_distribution: form.getValues() },
      }),
      defaultError: t('Sorry, an error occurred. Please try again.', {
        context: 'this is the default error message when submitting multi allocations for paycheck',
      }),
      isResponseSuccess: response => {
        const blockerDescriptors = response.response_context?.scenario_plan?.blocker_descriptors
        return !!blockerDescriptors?.length && blockerDescriptors?.length > 0
      },
    })
  }

  const backErrorMsg = t('Sorry, an error occurred. Please try again.', {
    context: 'this is the default error message when clicking back in multi allocations for paychecks',
  })

  useModalActionsOverride(
    useMemo(() => {
      const onBack = async () => {
        if (screen === 'allocationEdit') {
          setScreen('home')
        } else {
          await handleBlockerResponse({
            promisedBlockerUpdate: submitSetPaycheckMultiAllocation({
              cancel: {},
            }),
            defaultError: backErrorMsg,
            isResponseSuccess: response => {
              const blockerDescriptors = response.response_context?.scenario_plan?.blocker_descriptors
              return !!blockerDescriptors?.length && blockerDescriptors?.length > 0
            },
          })
        }
      }

      return journeyScope.target === 'fullscreen' ? (
        <ModalPaneHeader>
          <ModalBackAction onBack={onBack} />
        </ModalPaneHeader>
      ) : (
        <ModalActionHeader>
          <ModalCloseAction
            onClose={() => {
              journey.cancelAll()
            }}
          />
          <ModalBackAction onBack={onBack} />
        </ModalActionHeader>
      )
    }, [journey, submitSetPaycheckMultiAllocation, handleBlockerResponse, backErrorMsg, screen, journeyScope.target])
  )

  return (
    <BaseBlocker
      mainText={
        screen === 'allocationEdit' && allocationData ? allocationData.label : blocker.localized_title?.translated_value
      }
      form={form}
      onSubmit={handleSubmit}
      buttons={[]}
    >
      <Stack space={spacing.m}>
        {screen === 'home' ? (
          <>
            <PieChart
              hero={
                <HeroVariant hero={{ value: `${totalEditableAllocationPercentage}%`, label: 'of each paycheck' }} />
              }
              arcs={createArcs(arcs)}
            />
            {estimatedCurrencyAmountText && (
              <PaddingInset>
                <Text typography="body">{estimatedCurrencyAmountText}</Text>
              </PaddingInset>
            )}
            <PieChartLegend items={legend} type="percentage" />
            <Button size="large" onClick={handleSubmit}>
              {blocker.localized_cta_title?.translated_value}
            </Button>
          </>
        ) : null}
        {screen === 'allocationEdit' && allocationData?.label ? (
          <EditAllocation setScreen={setScreen} allocationData={allocationData} tintColors={allocation_tint_colors} />
        ) : null}
      </Stack>
      {isNoRemainingAllocationModalOpen ? (
        <NoRemainingAllocationModal onClose={() => setIsNoRemainingAllocationModalOpen(false)} />
      ) : null}
    </BaseBlocker>
  )
}
